/**
 * Guided setup — the rules, separated from the screen (BN-048).
 *
 * `SetupChooser` offered "Build step by step" with `comingSoon: true`, pointing at `/setup/build` —
 * a route that did not exist. Only `/setup/create` (a single-page club form) was routed.
 *
 * Every endpoint the wizard needs already exists, so this is composition, not new API. Two contract
 * facts drive everything below, and both were verified against the tree rather than assumed:
 *
 * 1. `CreateTeamBody.headCoachId` is `uuid.optional()` in the Zod schema, even though the route's
 *    TypeScript body type declares it required. Zod is the actual validator, so **teams can be
 *    created before any coach exists** — which is what makes a linear wizard possible at all.
 *
 * 2. `POST /api/players` throws `CoppaError` for any player under the org's consent age when no
 *    `parentId` is supplied (the age comes from `getOrgConsentAgeYears` — US 13 / EU 16 / AU 15, not
 *    a hardcoded 13). **This is a youth-soccer platform: most players are under that age.** So the
 *    spec's ordering — club → teams → players → coach invites — cannot work as written. A player
 *    step that just collects names would 400 for the majority of a real club's roster.
 *
 * The wizard therefore adds players as **parent + player pairs**: create the parent record first
 * (`POST /api/parents`, admin-gated, `userId` optional so a parent without an account is fine), then
 * the player with that `parentId`.
 *
 * The "is this child under the consent age" decision is NOT implemented here. Age comes from
 * `calculateAge` in `utils/age.ts` and the threshold from the jurisdiction catalog in
 * `@kantera/types` (`MAX_CONSENT_AGE_YEARS` when the club's own age is not yet known) — the same two
 * sources `PlayerCreate` uses, so the wizard and the single-player form cannot disagree. An earlier
 * draft of this file carried its own age arithmetic and its own default of 13; that was a second
 * source of truth waiting to drift.
 */

import { MAX_CONSENT_AGE_YEARS } from '@kantera/types';
import { calculateAge } from '../utils/age';

/** Steps, in the order the wizard walks them. */
export const SETUP_STEPS = ['club', 'teams', 'players', 'coaches'] as const;
export type SetupStep = (typeof SETUP_STEPS)[number];

export interface ClubDraft {
  name: string;
  city: string;
  state: string;
  timezone: string;
}

export interface TeamDraft {
  name: string;
  division: string;
  ageGroup: string;
  maxPlayers: number;
}

export interface PlayerDraft {
  firstName: string;
  lastName: string;
  /** YYYY-MM-DD — the server regex is exactly this. */
  dateOfBirth: string;
  ageGroup: string;
  position: string;
  preferredFoot: 'left' | 'right' | 'ambidextrous';
  /** Parent details. Required for an under-age player; see the COPPA note above. */
  parentFirstName: string;
  parentLastName: string;
  parentEmail: string;
}

export interface CoachInviteDraft {
  email: string;
  role: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Does this player need a parent record before `POST /api/players` will accept them?
 *
 * `consentAgeYears` is the club's own threshold from `GET /api/club-settings`. Until that has
 * loaded, callers pass nothing and get the STRICTEST age in the catalog, never the most permissive:
 * being asked for a parent unnecessarily costs a moment; not being asked registers a child without
 * consent.
 *
 * Errs toward YES on a bad date: `calculateAge` reports an unparseable, impossible or future date as
 * -1, and -1 is "unknown", not "adult". Guessing "adult" on a bad date is how a child gets created
 * without the COPPA gate. (This is stricter than a plain `age < threshold`, which would read -1 as
 * under age only by accident of arithmetic — the check is written out so the intent survives a
 * refactor.)
 */
export function requiresParent(
  dob: string,
  consentAgeYears: number = MAX_CONSENT_AGE_YEARS,
): boolean {
  const age = calculateAge(dob);
  return age < 0 || age < consentAgeYears;
}

export interface StepValidation {
  ok: boolean;
  errors: string[];
}

export function validateClub(c: ClubDraft): StepValidation {
  const errors: string[] = [];
  if (!c.name.trim()) errors.push('club.name');
  if (!c.city.trim()) errors.push('club.city');
  if (!c.state.trim()) errors.push('club.state');
  return { ok: errors.length === 0, errors };
}

export function validateTeam(t: TeamDraft): StepValidation {
  const errors: string[] = [];
  if (!t.name.trim()) errors.push('team.name');
  if (!t.division.trim()) errors.push('team.division');
  if (!t.ageGroup.trim()) errors.push('team.ageGroup');
  // Server: maxPlayers is int 1..50.
  if (!Number.isInteger(t.maxPlayers) || t.maxPlayers < 1 || t.maxPlayers > 50) {
    errors.push('team.maxPlayers');
  }
  return { ok: errors.length === 0, errors };
}

/**
 * Validate a player draft, including the parent fields when the child is under the consent age.
 *
 * This is the check that stops the wizard shipping a step that 400s for most of a youth roster.
 * `consentAgeYears` has the same meaning and default as in {@link requiresParent}.
 */
export function validatePlayer(
  p: PlayerDraft,
  consentAgeYears: number = MAX_CONSENT_AGE_YEARS,
): StepValidation {
  const errors: string[] = [];
  if (!p.firstName.trim()) errors.push('player.firstName');
  if (!p.lastName.trim()) errors.push('player.lastName');
  if (!ISO_DATE.test(p.dateOfBirth)) errors.push('player.dateOfBirth');
  if (!p.ageGroup.trim()) errors.push('player.ageGroup');
  if (!p.position.trim()) errors.push('player.position');

  if (requiresParent(p.dateOfBirth, consentAgeYears)) {
    if (!p.parentFirstName.trim()) errors.push('player.parentFirstName');
    if (!p.parentLastName.trim()) errors.push('player.parentLastName');
    if (!EMAIL.test(p.parentEmail)) errors.push('player.parentEmail');
  }

  return { ok: errors.length === 0, errors };
}

export function validateCoachInvite(c: CoachInviteDraft): StepValidation {
  const errors: string[] = [];
  if (!EMAIL.test(c.email)) errors.push('coach.email');
  if (!c.role.trim()) errors.push('coach.role');
  return { ok: errors.length === 0, errors };
}

/** Request bodies, shaped to the server schemas exactly. */
export const toClubBody = (c: ClubDraft) => ({
  name: c.name.trim(),
  city: c.city.trim(),
  state: c.state.trim(),
  country: 'US',
  timezone: c.timezone,
});

export const toTeamBody = (t: TeamDraft, clubId: string) => ({
  clubId,
  name: t.name.trim(),
  division: t.division.trim(),
  ageGroup: t.ageGroup.trim(),
  maxPlayers: t.maxPlayers,
});

export const toParentBody = (p: PlayerDraft) => ({
  firstName: p.parentFirstName.trim(),
  lastName: p.parentLastName.trim(),
  email: p.parentEmail.trim(),
});

export const toPlayerBody = (p: PlayerDraft, parentId: string | null) => ({
  firstName: p.firstName.trim(),
  lastName: p.lastName.trim(),
  dateOfBirth: p.dateOfBirth,
  ageGroup: p.ageGroup.trim(),
  position: p.position.trim(),
  preferredFoot: p.preferredFoot,
  ...(parentId ? { parentId } : {}),
});

export const toInvitationBody = (c: CoachInviteDraft) => ({
  email: c.email.trim(),
  role: c.role,
});
